import { Client as ColyseusClient, type Room as ColyseusRoom } from 'colyseus.js';
import { Player, generatePlayerId } from './player';
import { Room } from './room';
import type { TeamConfig } from '../logic/team';
import type { InputSync, GameEvent, GameStateMessage } from './gameStateSync';
import type { GameSettings } from '../core/gameSettings';
import {
  PROTOCOL_VERSION,
  ROOM_TYPE,
  ERR_KICKED,
  MSG,
  type JoinOptions,
  type RoomStateSync,
  type GameStartBroadcast,
  type PlayerAction,
} from './protocol';

export type NetworkRole = 'host' | 'client' | 'offline';
export type ConnectionQuality = 'good' | 'degraded' | 'poor' | 'dead';

export type { RoomStateSync };

export interface NetworkEventHandler {
  onPlayerJoined?: (player: Player) => void;
  onPlayerLeft?: (playerId: string) => void;
  onPlayerReconnected?: (player: Player) => void;
  onTeamClaimed?: (playerId: string, teamId: string) => void;
  onTeamReleased?: (playerId: string, teamId: string) => void;
  onGameStart?: (terrainSeed?: number, teams?: TeamConfig[], settings?: GameSettings) => void;
  onStateSync?: (state: RoomStateSync) => void;
  onGameStateReceived?: (message: GameStateMessage) => void;
  onGameInputReceived?: (input: InputSync) => void;
  onGameEventReceived?: (event: GameEvent) => void;
  onReconnecting?: (attempt: number, maxAttempts: number) => void;
  onReconnected?: () => void;
  onReconnectFailed?: (reason: string) => void;
  onConnectionStateChanged?: (state: 'connected' | 'reconnecting' | 'disconnected') => void;
  onError?: (error: string) => void;
}

const PLAYER_ID_STORAGE_KEY = 'worms_player_id';
const ROOM_CODE_STORAGE_KEY = 'worms_last_room_code';
const PING_INTERVAL_MS = 2000;
const RTT_SAMPLE_SIZE = 5;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 8000;

function resolveServerEndpoint(): string {
  const envUrl = import.meta.env?.VITE_COLYSEUS_URL as string | undefined;
  if (envUrl) return envUrl;
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // Vite dev server (client on :3000) talks to the game server on :2567.
  if (import.meta.env?.DEV) {
    return `${proto}://${window.location.hostname}:2567`;
  }
  // Production single-container build: the game server also serves the
  // client, so connect same-origin (the reverse proxy forwards WS +
  // matchmaking to it). `window.location.host` keeps any non-default port.
  return `${proto}://${window.location.host}`;
}

/**
 * Client connection to the VVorms game server (Colyseus). Keeps the public
 * API of the old PeerJS-based manager so the lobby UI and Game integrate
 * unchanged: the server is now the room authority; this class mirrors the
 * authoritative room state locally and forwards intents.
 */
export class NetworkManager {
  private localPlayer: Player;
  private room: Room | null = null;
  private colyseus: ColyseusClient;
  private serverRoom: ColyseusRoom | null = null;
  private role: NetworkRole = 'offline';
  private eventHandlers: NetworkEventHandler = {};

  private roomCode = '';
  private highestReceivedSyncSeq = 0;
  private receivedFirstSync = false;
  private intentionalLeave = false;
  private reconnectionToken = '';
  private connectionState: 'connected' | 'reconnecting' | 'disconnected' = 'disconnected';
  private disconnectedPlayers = new Set<string>();

  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private rttSamples: number[] = [];
  private lastPongAt = 0;

  constructor(playerName: string) {
    const persistedId = this.getPersistedPlayerId();

    this.localPlayer = new Player({
      id: persistedId || generatePlayerId(),
      name: playerName,
      isLocal: true,
    });

    this.persistPlayerId(this.localPlayer.id);
    this.colyseus = new ColyseusClient(resolveServerEndpoint());
  }

  // ----- persistence -----

  private getPersistedPlayerId(): string | null {
    try {
      return localStorage.getItem(PLAYER_ID_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private persistPlayerId(id: string): void {
    try {
      localStorage.setItem(PLAYER_ID_STORAGE_KEY, id);
    } catch {
      console.warn('Failed to persist player ID');
    }
  }

  private persistRoomCode(code: string): void {
    try {
      localStorage.setItem(ROOM_CODE_STORAGE_KEY, code);
    } catch {
      console.warn('Failed to persist room code');
    }
  }

  getLastRoomCode(): string | null {
    try {
      return localStorage.getItem(ROOM_CODE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  clearPersistedRoomCode(): void {
    try {
      localStorage.removeItem(ROOM_CODE_STORAGE_KEY);
    } catch {
      console.warn('Failed to clear room code');
    }
  }

  // ----- accessors -----

  getLocalPlayer(): Player {
    return this.localPlayer;
  }

  getRoom(): Room | null {
    return this.room;
  }

  getRole(): NetworkRole {
    return this.role;
  }

  isHost(): boolean {
    return this.role === 'host';
  }

  getRoomCode(): string {
    return this.roomCode;
  }

  setEventHandlers(handlers: NetworkEventHandler): void {
    this.eventHandlers = handlers;
  }

  addEventHandlers(handlers: Partial<NetworkEventHandler>): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  getEventHandlers(): NetworkEventHandler {
    return this.eventHandlers;
  }

  getConnectionState(): 'connected' | 'reconnecting' | 'disconnected' {
    return this.connectionState;
  }

  getPeerCount(): number {
    return Math.max(0, (this.room?.playerCount ?? 1) - 1);
  }

  getAverageRTT(): number {
    if (this.rttSamples.length === 0) return 0;
    return this.rttSamples.reduce((a, b) => a + b, 0) / this.rttSamples.length;
  }

  getConnectionQuality(): ConnectionQuality {
    if (this.connectionState !== 'connected') return 'dead';
    if (this.lastPongAt > 0 && performance.now() - this.lastPongAt > 5000) return 'dead';
    const rtt = this.getAverageRTT();
    if (rtt === 0) return 'good';
    if (rtt < 100) return 'good';
    if (rtt < 300) return 'degraded';
    return 'poor';
  }

  getConnectionInfo(): { type: string; isIPv6: boolean; isRelay: boolean } | null {
    if (this.connectionState === 'disconnected') return null;
    return { type: 'Server (WS)', isIPv6: false, isRelay: false };
  }

  // ----- room lifecycle -----

  private joinOptions(roomName?: string): JoinOptions & { roomName?: string } {
    return {
      playerId: this.localPlayer.id,
      playerName: this.localPlayer.name,
      protocolVersion: PROTOCOL_VERSION,
      roomName,
    };
  }

  async createRoom(roomName: string, _teamConfigs: TeamConfig[]): Promise<string> {
    this.role = 'host';
    this.intentionalLeave = false;

    const serverRoom = await this.colyseus.create(ROOM_TYPE, this.joinOptions(roomName));
    this.adoptServerRoom(serverRoom);
    await this.waitForFirstSync();

    this.persistRoomCode(this.roomCode);
    return this.roomCode;
  }

  async joinRoom(roomCode: string): Promise<{ roomName: string }> {
    this.role = 'client';
    this.intentionalLeave = false;

    let serverRoom: ColyseusRoom;
    try {
      serverRoom = await this.colyseus.joinById(roomCode, this.joinOptions());
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (/not found|locked|no rooms/i.test(message)) {
        throw new Error('Room not found. Check the code and try again.');
      }
      throw new Error(message);
    }

    this.adoptServerRoom(serverRoom);
    await this.waitForFirstSync();

    this.persistRoomCode(roomCode);
    return { roomName: this.room?.name ?? roomCode };
  }

  private adoptServerRoom(serverRoom: ColyseusRoom): void {
    this.serverRoom = serverRoom;
    this.roomCode = serverRoom.roomId;
    this.reconnectionToken = serverRoom.reconnectionToken;
    this.connectionState = 'connected';
    this.bindMessages(serverRoom);
    this.startPing();
  }

  private waitForFirstSync(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Timeout waiting for room state')), 10000);
      const poll = () => {
        if (this.receivedFirstSync) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(poll, 25);
        }
      };
      poll();
    });
  }

  private bindMessages(serverRoom: ColyseusRoom): void {
    serverRoom.onMessage(MSG.roomState, (state: RoomStateSync) => {
      if (this.applyStateSync(state)) {
        this.receivedFirstSync = true;
        this.eventHandlers.onStateSync?.(state);
      }
    });

    serverRoom.onMessage(MSG.gameStart, (message: GameStartBroadcast) => {
      this.room?.syncState('playing');
      this.eventHandlers.onGameStart?.(message.terrainSeed, message.teams, message.settings);
    });

    serverRoom.onMessage(MSG.kicked, (message: { reason?: string }) => {
      this.handlePlayerKicked(message?.reason ?? 'Removed by host');
    });

    serverRoom.onMessage(MSG.gameState, (message: GameStateMessage) => {
      this.eventHandlers.onGameStateReceived?.(message);
    });

    serverRoom.onMessage(MSG.gameEvent, (event: GameEvent) => {
      this.eventHandlers.onGameEventReceived?.(event);
    });

    serverRoom.onMessage(MSG.pong, (message: { t?: number }) => {
      this.handlePong(message?.t ?? 0);
    });

    serverRoom.onError((code, message) => {
      console.error('[NetworkManager] Server room error:', code, message);
      this.eventHandlers.onError?.(message ?? `Server error ${code}`);
    });

    serverRoom.onLeave((code) => {
      this.handleLeave(code);
    });
  }

  // ----- lobby intents -----

  claimTeam(teamId: string): boolean {
    if (!this.room) return false;
    const ok = this.room.claimTeam(this.localPlayer.id, teamId);
    this.serverRoom?.send(MSG.claimTeam, { teamId });
    return ok;
  }

  releaseTeam(teamId: string): boolean {
    if (!this.room) return false;
    const ok = this.room.releaseTeam(this.localPlayer.id, teamId);
    this.serverRoom?.send(MSG.releaseTeam, { teamId });
    return ok;
  }

  toggleSpectator(isSpectator: boolean): boolean {
    if (!this.room) return false;
    const ok = this.room.setPlayerSpectator(this.localPlayer.id, isSpectator);
    this.serverRoom?.send(MSG.spectator, { isSpectator });
    return ok;
  }

  broadcastMaxTeamsPerPlayer(value: number): void {
    this.serverRoom?.send(MSG.maxTeams, { value });
  }

  broadcastTeamNameChanged(teamId: string, newName: string): void {
    this.serverRoom?.send(MSG.teamName, { teamId, name: newName });
  }

  setAllianceCount(count: number): void {
    this.room?.setAllianceCount(count);
    this.serverRoom?.send(MSG.allianceCount, { count });
  }

  randomizeAlliances(): void {
    // Random is non-deterministic: online, let the server roll and broadcast
    // (avoid a client/server mismatch); offline, roll locally.
    if (this.serverRoom) {
      this.serverRoom.send(MSG.randomizeAlliances, {});
    } else {
      this.room?.randomizeAlliances();
    }
  }

  setTeamAlliance(teamId: string, allianceId: string): void {
    this.room?.setTeamAlliance(teamId, allianceId);
    this.serverRoom?.send(MSG.teamAlliance, { teamId, allianceId });
  }

  setTankType(teamId: string, tankType: string): void {
    this.room?.setTankType(teamId, tankType);
    this.serverRoom?.send(MSG.tankType, { teamId, tankType });
  }

  /**
   * Request a game start. The actual start is driven by the server's
   * 'game-start' broadcast (delivered to every player, including the host)
   * via the onGameStart handler.
   */
  startGame(terrainSeed?: number, settings?: GameSettings): boolean {
    if (!this.isHost() || !this.room?.canStart()) return false;
    this.serverRoom?.send(MSG.startGame, { terrainSeed, settings });
    return true;
  }

  kickPlayer(playerId: string): boolean {
    if (!this.isHost() || playerId === this.localPlayer.id) return false;
    this.serverRoom?.send(MSG.kick, { playerId });
    return true;
  }

  // ----- in-game messages (server-authoritative) -----

  /** Send a discrete player intent to the server simulation. */
  sendPlayerAction(action: PlayerAction): void {
    this.serverRoom?.send(MSG.playerAction, action);
  }

  /** Ask the server for a fresh keyframe (e.g. after a delta gap). */
  requestKeyframe(): void {
    this.serverRoom?.send(MSG.requestKeyframe, {});
  }

  // ----- authoritative state mirroring -----

  private applyStateSync(state: RoomStateSync): boolean {
    if (state.seq <= this.highestReceivedSyncSeq) {
      return false;
    }
    this.highestReceivedSyncSeq = state.seq;

    if (!this.room) {
      this.room = new Room({
        id: state.roomId,
        name: state.roomName,
        maxPlayers: 8,
        hostId: state.hostId,
      });
    }
    const room = this.room;

    room.maxTeamsPerPlayer = state.maxTeamsPerPlayer;
    if (typeof state.allianceCount === 'number') room.allianceCount = state.allianceCount;

    for (const teamSlot of state.teamSlots) {
      const existing = room.getTeamSlots().find((s) => s.config.id === teamSlot.config.id);
      if (!existing) {
        room.addTeamSlot(teamSlot.config);
      } else {
        existing.config.name = teamSlot.config.name;
        existing.config.allianceId = teamSlot.config.allianceId ?? existing.config.allianceId;
        // First worm is renamed to the claiming player; mirror it for display.
        existing.config.wormNames = [...teamSlot.config.wormNames];
      }
    }

    const previousIds = new Set(room.getAllPlayers().map((p) => p.id));
    const syncedIds = new Set(state.players.map((p) => p.id));

    // Adding players requires the lobby state; apply the room phase afterwards.
    room.syncState('lobby');

    for (const playerData of state.players) {
      let player = room.getPlayer(playerData.id);
      if (!player) {
        player = new Player({
          id: playerData.id,
          name: playerData.name,
          isLocal: playerData.id === this.localPlayer.id,
        });
        room.addPlayer(player);
        if (this.receivedFirstSync && playerData.id !== this.localPlayer.id) {
          this.eventHandlers.onPlayerJoined?.(player);
        }
      }
      player.teamIds = [...playerData.teamIds];
      player.ready = playerData.ready;
      player.isSpectator = playerData.isSpectator;

      if (playerData.id === this.localPlayer.id) {
        this.localPlayer.teamIds = [...playerData.teamIds];
        this.localPlayer.ready = playerData.ready;
        this.localPlayer.isSpectator = playerData.isSpectator;
      }
    }

    for (const id of previousIds) {
      if (!syncedIds.has(id)) {
        room.removePlayer(id);
        this.eventHandlers.onPlayerLeft?.(id);
      }
    }

    for (const syncedSlot of state.teamSlots) {
      room.setTeamSlotOwner(syncedSlot.config.id, syncedSlot.ownerId);
    }

    room.syncState(state.state);
    this.disconnectedPlayers = new Set(state.disconnectedPlayers);
    return true;
  }

  // ----- connection lifecycle -----

  private handlePlayerKicked(reason: string): void {
    alert(`You have been removed from the lobby: ${reason}`);
    this.disconnect();
    this.eventHandlers.onPlayerLeft?.(this.localPlayer.id);
  }

  private handleLeave(code: number): void {
    this.stopPing();
    this.serverRoom = null;

    if (this.intentionalLeave || code === ERR_KICKED) {
      this.connectionState = 'disconnected';
      this.eventHandlers.onConnectionStateChanged?.('disconnected');
      return;
    }

    console.log(`[NetworkManager] Connection lost (code ${code}), attempting reconnect...`);
    this.connectionState = 'reconnecting';
    this.eventHandlers.onConnectionStateChanged?.('reconnecting');
    void this.attemptReconnect();
  }

  private async attemptReconnect(): Promise<void> {
    for (let attempt = 1; attempt <= MAX_RECONNECT_ATTEMPTS; attempt++) {
      this.eventHandlers.onReconnecting?.(attempt, MAX_RECONNECT_ATTEMPTS);
      try {
        const serverRoom = await this.colyseus.reconnect(this.reconnectionToken);
        this.adoptServerRoom(serverRoom);
        console.log('[NetworkManager] Reconnected to server');
        this.eventHandlers.onConnectionStateChanged?.('connected');
        this.eventHandlers.onReconnected?.();
        return;
      } catch (e) {
        console.warn(`[NetworkManager] Reconnect attempt ${attempt} failed:`, e);
        if (this.intentionalLeave) return;
        const delay = Math.min(RECONNECT_DELAY_BASE * 2 ** (attempt - 1), RECONNECT_DELAY_MAX);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    this.connectionState = 'disconnected';
    this.eventHandlers.onReconnectFailed?.('Could not reconnect to the server');
    this.eventHandlers.onConnectionStateChanged?.('disconnected');
  }

  private startPing(): void {
    this.stopPing();
    this.lastPongAt = performance.now();
    this.pingTimer = setInterval(() => {
      this.serverRoom?.send(MSG.ping, { t: performance.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private handlePong(sentAt: number): void {
    this.lastPongAt = performance.now();
    if (sentAt > 0) {
      this.rttSamples.push(performance.now() - sentAt);
      if (this.rttSamples.length > RTT_SAMPLE_SIZE) {
        this.rttSamples.shift();
      }
    }
  }

  disconnect(): void {
    this.intentionalLeave = true;
    this.stopPing();
    this.serverRoom?.leave(true);
    this.serverRoom = null;
    this.room = null;
    this.role = 'offline';
    this.roomCode = '';
    this.connectionState = 'disconnected';
    this.highestReceivedSyncSeq = 0;
    this.receivedFirstSync = false;
    this.rttSamples = [];
    this.disconnectedPlayers.clear();
    this.clearPersistedRoomCode();
  }
}
